import axios from "axios";
import React, { useState, useEffect } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { toast } from "react-toastify";

const DemoModal = ({ setIsModalOpen, setIsSuccessModalOpen }) => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    countryCode: "+91",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [countryCodes, setCountryCodes] = useState([]);
  const [selectedFlag, setSelectedFlag] = useState(
    "https://flagcdn.com/w320/in.png"
  );
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        const response = await fetch("https://restcountries.com/v3.1/all");
        const data = await response.json();

        const codes = data
          .map((country) => {
            if (!country.idd || !country.idd.root || !country.cca2) return null;
            return {
              code: `${country.idd.root}${
                country.idd.suffixes ? country.idd.suffixes[0] : ""
              }`,
              flag: country.flags?.svg || "",
              name: country.cca2, // Country code (e.g., IN, US)
            };
          })
          .filter((c) => c !== null);

        setCountryCodes(codes);
      } catch (error) {
        console.error("Error fetching country codes:", error);
      }
    };

    fetchCountryCodes();
  }, []);

  const handleCountryChange = (e) => {
    const newCode = e.target.value;
    const selectedCountry = countryCodes.find((c) => c.code === newCode);
    setFormData({ ...formData, countryCode: newCode });
    setSelectedFlag(selectedCountry ? selectedCountry.flag : "");
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });

    // ✅ Real-time validation
    validateField(e.target.name, e.target.value);
  };

  // ✅ Validation Functions
  const validateField = (name, value) => {
    let error = "";

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) error = "Invalid email format";
    }

    if (name === "phone") {
      const phoneRegex = /^[0-9]{10,15}$/;
      if (!phoneRegex.test(value))
        error = "Invalid phone number (10-15 digits only)";
    }

    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate fields
    validateField("email", formData.email);
    validateField("phone", formData.phone);

    if (Object.values(errors).some((err) => err)) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post("/request_demo", formData, {
        headers: {
          "Content-Type": "application/json",
        },
        validateStatus: (status) => {
          return status >= 200 && status < 300;
        },
      });

      // Reset form
      setFormData({
        name: "",
        company: "",
        email: "",
        countryCode: "+91",
        phone: "",
        message: "",
      });
      setErrors({});

      // Close form modal and show success
      setIsModalOpen(false);
      // setIsSuccessModalOpen(true);
      toast.success(
        "Thanks! Let’s shape the future — we’ll reach out shortly."
      );
    } catch (error) {
      console.error("Submission error:", error);

      // Differentiate between server errors and network errors
      if (error.response) {
        // Server responded with error status
        toast.error(
          ` ${
            error.response.data.message ||
            "Oops! That email’s already part of our network. Got another one?"
          }`
        );
      } else if (error.request) {
        // Request was made but no response received
        toast.error("Network error - please check your connection");
      } else {
        // Other errors
        toast.error("Error submitting form");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4  bg-black bg-opacity-50">
        <div className="bg-white w-full max-w-md rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
          {/* Header */}

          <div className="sticky top-0 bg-white z-10 px-6 pt-4 pb-4">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-orange-700 hover:text-blue-700 focus:outline-none "
              aria-label="Close modal"
            >
              <AiOutlineClose className="text-3xl font-extrabold  " />
            </button>

            {/* Content with proper spacing */}
            <div className="py-4  border-b-4 border-gray-200">
              {" "}
              {/* Added padding-top to make space for close button */}
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                Claim Your Free AI Consultation!
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Our expert team is here to guide you through the possibilities
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="px-6 pb-10">
            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* Name */}
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  placeholder="Name*"
                  required
                />
              </div>

              {/* Company */}
              <div>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  placeholder="Company/Institute"
                />
              </div>

              {/* Email */}
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  placeholder="Email ID*"
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1 flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
                    <img
                      src={selectedFlag}
                      alt="Flag"
                      className="w-5 h-4 ml-3"
                    />
                    <select
                      className="flex-1 px-3 py-3 bg-transparent outline-none appearance-none cursor-pointer"
                      value={formData.countryCode}
                      onChange={handleCountryChange}
                    >
                      {countryCodes.map((country, index) => (
                        <option key={index} value={country.code}>
                          {country.code}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-3">
                      <svg
                        className="w-4 h-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="flex-2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    placeholder="Phone Number*"
                    required
                  />
                </div>
                {errors.phone && (
                  <p className="text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  placeholder="Your Message"
                  rows="2"
                ></textarea>
              </div>

              {/* No Strings Attached */}
              <div>
                <p className="text-gray-700">
                  <span className="font-semibold text-[#ef8e38]">
                    No Strings Attached:
                  </span>{" "}
                  This consultation is free!
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium py-3 px-6 rounded-lg hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  "Submit"
                )}
              </button>

              {/* Response Message */}
              {responseMessage && (
                <div
                  className={`p-3 rounded-lg ${
                    responseMessage.includes("success")
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-700"
                  }`}
                >
                  {responseMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemoModal;
